import "./Auth.scss";
import React, { useRef, useState } from "react";
import OTPInput from "react-otp-input";
import { otpMailIcon, otpMobileIcon } from "assets/icons";
import { Button } from "components/form";
import useResendOtp from "hooks/useResendOtp";
import useButtonLoader from "hooks/useButtonLoader";
import { toastMessage } from "utils/toastMessageHelper";
import { resendOtpApi, verifyOtp } from "services/auth";
import { setToLocal } from "config/axiosInstance";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
// import { requestForToken } from "config/firebase";

const OTPScreen = ({ type, inputData = {}, closeModal, setInputData }) => {
  const navigateRoute = useNavigate();

  const { t } = useTranslation();

  // Set all value
  const [otp, setOtp] = useState({
    mobile: "",
    email: "",
  });
  const [errors, setErrors] = useState({
    mobile: false,
    email: false,
  });

  // Use Hook
  const [otpButtonLoader, setOtpButtonLoader] = useButtonLoader(
    t("OTP_SCREEN.continue")
  );

  // Separate refs for mobile and email OTP resend text
  const mobileResendTextRef = useRef();
  const emailResendTextRef = useRef();

  // Separate useResendOtp hook instances for mobile and email OTP
  const { canResend: canResendMobile, resetTimer: resetTimerMobile } =
    useResendOtp(mobileResendTextRef);
  const { canResend: canResendEmail, resetTimer: resetTimerEmail } =
    useResendOtp(emailResendTextRef);

  // for OTP screen
  const otpScreen = {
    login: [
      {
        otpType: "mobile",
        image: otpMobileIcon,
        isRegister: true,
        firstTitle: t("OTP_SCREEN.verify-phone-number"),
        secondTitle: t("OTP_SCREEN.enter-verification-code"),
        thirdTitle: t("OTP_SCREEN.enter-code"),
        fourthTitle: t("OTP_SCREEN.dont-get-code"),
        errorMessage: t("OTP_SCREEN.please-enter-otp"),
        data: `+966-${inputData["mobile"]}`,
      },
    ],
    signup: [
      {
        isEnable: false,
        otpType: "email",
        image: otpMailIcon,
        isRegister: false,
        firstTitle: t("OTP_SCREEN.verify-email-address"),
        secondTitle: t("OTP_SCREEN.email-verification"),
        thirdTitle: t("OTP_SCREEN.enter-code"),
        fourthTitle: t("OTP_SCREEN.dont-get-email"),
        errorMessage: t("OTP_SCREEN.enter-email-otp"),
        data: `${inputData["email"]}`,
      },
      {
        isEnable: true,
        otpType: "mobile",
        image: otpMobileIcon,
        isRegister: false,
        firstTitle: t("OTP_SCREEN.verify-phone-number"),
        secondTitle: t("OTP_SCREEN.enter-verification-code"),
        thirdTitle: t("OTP_SCREEN.enter-code"),
        fourthTitle: t("OTP_SCREEN.dont-get-code"),
        errorMessage: t("OTP_SCREEN.please-enter-otp"),
        data: `+966-${inputData["mobile"]}`,
      },
    ].filter((el) => el.isEnable),
  }[type || "signup"];

  // close OTP modal
  const closeOtpModal = () => {
    setOtp({});
    setErrors({});
    closeModal();
  };

  // handle Input otp change
  const handleOtpChange = (otpValue, otpType) => {
    setOtp((prevOtp) => ({
      ...prevOtp,
      [otpType]: otpValue,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [otpType]: false,
    }));
  };

  // get error input style
  const getInputStyle = (otpType) => {
    // Determine if there's an error for the current OTP type
    const hasError = errors[otpType];

    // Return the combined style for the input
    return {
      borderColor: hasError ? "#dc3545" : "initial", // Red border if error
      borderWidth: "2px", // Consistent border width
    };
  };

  // Set the correct ref for the OTP resend text based on the OTP type
  const handleResendTextRef = (otpType) => {
    if (otpType === "mobile") {
      return mobileResendTextRef;
    } else if (otpType === "email") {
      return emailResendTextRef;
    }
    return null; // Default case
  };

  // verify OTP handler
  const verifyOtphandler = async (inputData) => {
    // Validate OTP lengths
    const mobileOtpValid = otp?.mobile?.length === 5;
    const emailOtpValid = type === "signup" ? otp?.email?.length === 5 : true;

    // Code By Rahul
    // if (!mobileOtpValid || !emailOtpValid) {
    //   setErrors((prevErrors) => ({
    //     ...prevErrors,
    //     mobile: !mobileOtpValid,
    //     email: type === "signup" && !emailOtpValid,
    //   }));
    //   return;
    // }

    // Code by himanshu
    if (!mobileOtpValid) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        mobile: !mobileOtpValid,
        // email: type === "signup" && !emailOtpValid,
      }));
      return;
    }

    try {
      setOtpButtonLoader(true);

      // Build request data
      const reqData = {
        ...inputData,
        ...(otp?.mobile && { mobileOTP: otp.mobile }),
        // fcmToken: await requestForToken(),
        deviceType: "web",
        // ...(type === "signup" && otp?.email && { emailOTP: otp.email }),
        // ...(type === "signup" && otp?.email && { emailOTP: otp.email }),
      };

      // Send verification OTP request
      const { data = {}, message = "", status } = await verifyOtp(reqData);

      if (status === 200) {
        toastMessage(message, "success", "verifyOtp");
        if (type === "login") {
          navigateRoute(`/reset-password/${data.token}`);
        } else {
          setToLocal("token", data.token);
          setToLocal("currentUserRole", "researcher");
          navigateRoute("/dashboard/create-survey");
        }
        setInputData({});
        closeOtpModal();
      }
    } catch (e) {
      console.error("OTP verification error:", e);
    } finally {
      setOtpButtonLoader(false);
    }
  };

  // Resend OTP handler
  const resendOtpHandler = async (otpType) => {
    try {
      setOtpButtonLoader(true);
      const reqData = { [otpType]: inputData[otpType] };
      const { message = "", status } = await resendOtpApi(reqData);
      if (status === 200) {
        otpType === "mobile" ? resetTimerMobile() : resetTimerEmail();
        toastMessage(message, "success", "verifyOtp");
      }
    } catch (e) {
      console.log("error", e);
    } finally {
      setOtpButtonLoader(false);
    }
  };

  return (
    <>
      {otpScreen &&
        otpScreen.length > 0 &&
        otpScreen.map(
          (
            {
              image,
              firstTitle,
              secondTitle,
              thirdTitle,
              fourthTitle,
              isRegister,
              otpType,
              data,
            },
            index
          ) => (
            <div
              key={index}
              className={`otp_screen`}
              // className={`otp_screen ${type === "signup" && "split"}`}
            >
              <img src={image} alt="" className="image" />
              <h5>{firstTitle}</h5>
              <p>
                {secondTitle}
                <b>{data}</b>
              </p>
              <div className="opt_wrapper">
                <p>{thirdTitle}</p>
                <OTPInput
                  value={otp[otpType]}
                  onChange={(otpValue) => handleOtpChange(otpValue, otpType)}
                  numInputs={5}
                  containerStyle={"opt-input"}
                  inputStyle={"input form-control "}
                  renderInput={(props) => (
                    <input
                      {...props}
                      style={{
                        ...props.style,
                        ...getInputStyle(otpType), // Merge custom styles with props
                      }}
                    />
                  )}
                  inputType={"tel"}
                />
                {(otpType === "mobile" ? canResendMobile : canResendEmail) && (
                  <p className="resend_button">
                    {fourthTitle}
                    <button
                      type="button"
                      className="resent"
                      onClick={() => resendOtpHandler(otpType)}
                    >
                      {t("OTP_SCREEN.resend")}
                    </button>
                  </p>
                )}
                <div ref={handleResendTextRef(otpType)}>
                  {/* Timer text will be updated here based on OTP type */}
                </div>
              </div>
            </div>
          )
        )}
      <div className="button_wrap">
        <Button
          className={"w-100"}
          type="button"
          ref={otpButtonLoader}
          onClick={() => verifyOtphandler(inputData)}
        >
          {t("OTP_SCREEN.continue")}
        </Button>
      </div>
    </>
  );
};

export default OTPScreen;
